import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Mail, Search, Award, TrendingUp, FileText, Shield } from "lucide-react";
import SEO from "../components/SEO";
import { getBreadcrumbSchema } from "../utils/schema";
import "./Blog.css";

export const Blog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All Posts");

  // Analytics tracking function
  const trackEvent = (eventName, properties) => {
    // Use gtag if available (Google Analytics)
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, properties);
    }
    // Fallback to console logging for development
    console.log(`[Analytics] ${eventName}:`, properties);
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' }
  ]);

  // Featured article
  const featuredPost = {
    id: 1,
    title: "Tax Deadlines for Small Businesses in 2026/27",
    excerpt: "Stay ahead of HMRC deadlines, avoid penalties, and never miss a filing again with this practical guide for small businesses and sole traders.",
    category: "Tax / Compliance / Small Business",
    date: "2 April 2026",
    readTime: "8 min read",
    author: "Brightson Accounting",
    credentials: "FCCA Qualified",
    slug: "tax-deadlines-small-businesses-2026-27",
    featured: true
  };

  // Blog posts
  const blogPosts = [
    {
      id: 2,
      title: "How to Reduce Your Corporation Tax Bill Legally",
      excerpt: "Discover legitimate tax-saving strategies that can help your limited company reduce corporation tax while staying fully compliant.",
      category: "Tax Advisory",
      date: "2 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "reduce-corporation-tax-uk",
      popular: true,
      featuredInSection: true
    },
    {
      id: 3,
      title: "Self Assessment Guide for First-Time Filers",
      excerpt: "Everything you need to know about filing your first self assessment tax return, from registration to submission.",
      category: "Self Assessment",
      date: "2 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "self-assessment-guide-first-time",
      popular: true,
      featuredInSection: true
    },
    {
      id: 4,
      title: "Making Tax Digital: What Small Businesses Need to Know",
      excerpt: "Understanding MTD requirements for VAT and income tax, plus how to prepare your business for digital record-keeping.",
      category: "Compliance",
      date: "28 December 2025",
      readTime: "8 min read",
      author: "Sonali Mitra",
      credentials: "FCCA",
      slug: "making-tax-digital-guide",
      popular: true
    },
    {
      id: 5,
      title: "How to Start a Business in the UK (2026 Step-by-Step Guide)",
      excerpt: "Complete step-by-step guide to starting a business in the UK in 2026. Registration, tax setup, and compliance explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "start-business-uk-2026",
      popular: true
    },
    {
      id: 6,
      title: "Sole Trader vs Limited Company (UK 2026): Which Is Better?",
      excerpt: "Compare sole trader and limited company structures. Tax, liability, costs, and admin explained for UK business owners.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "sole-trader-vs-limited-company-uk",
      popular: true
    },
    {
      id: 7,
      title: "How to Register as Self-Employed in the UK (2026 Guide)",
      excerpt: "Step-by-step guide to registering as self-employed with HMRC. Deadlines, requirements, and tax setup explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "register-self-employed-uk"
    },
    {
      id: 8,
      title: "Business Startup Costs You Can Claim (UK Tax Guide)",
      excerpt: "Discover which startup costs are tax-deductible for UK businesses. Pre-trading expenses, capital allowances, and HMRC rules explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "startup-costs-tax-uk"
    },
    {
      id: 9,
      title: "Do You Need to Register for VAT? (UK 2026 Explained)",
      excerpt: "VAT registration thresholds, deadlines, and voluntary registration explained for UK businesses.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "vat-registration-uk"
    },
    {
      id: 10,
      title: "How Much Tax Will I Pay as a New Business Owner?",
      excerpt: "Complete tax guide for new UK business owners. Income Tax, National Insurance, Corporation Tax, and tax-saving strategies explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-tax-uk-guide"
    },
    {
      id: 11,
      title: "Business Bank Account: Do You Really Need One?",
      excerpt: "Do sole traders and limited companies need a business bank account? Legal requirements, benefits, and best options explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-bank-account-uk"
    },
    {
      id: 12,
      title: "Common Mistakes New Business Owners Make (UK Guide)",
      excerpt: "Learn the most common mistakes new UK business owners make and how to avoid them. Tax, compliance, cash flow, and setup errors explained.",
      category: "Starting a Business",
      date: "10 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-mistakes-uk"
    },
    {
      id: 13,
      title: "How to Scale Your Business Without Losing Control (UK 2026 Guide)",
      excerpt: "Practical strategies for scaling your UK business while maintaining quality, profitability, and control.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "scale-business-uk",
      popular: true
    },
    {
      id: 14,
      title: "Cash Flow Management for Growing Businesses (UK Guide)",
      excerpt: "Master cash flow management for your growing UK business. Practical strategies to avoid cash crunches and maintain healthy finances.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "cash-flow-management-uk",
      popular: true
    },
    {
      id: 15,
      title: "Tax Planning Strategies for Growing Businesses (UK 2026)",
      excerpt: "Strategic tax planning for growing UK businesses. Reduce Corporation Tax legally while scaling profitably.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "tax-planning-growing-business-uk",
      popular: true
    },
    {
      id: 16,
      title: "Salary vs Dividends: What's Best for Directors in 2026?",
      excerpt: "Complete guide to salary vs dividends for UK limited company directors. Tax-efficient income extraction strategies explained.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "salary-vs-dividends-uk-2026",
      popular: true
    },
    {
      id: 17,
      title: "When Should You Hire an Accountant? (UK Guide)",
      excerpt: "Know when to hire an accountant for your UK business. Cost vs benefit, what to expect, and how to choose the right accountant.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "when-to-hire-accountant-uk",
      popular: true
    },
    {
      id: 18,
      title: "How to Grow Your Business Profitably (Not Just Bigger)",
      excerpt: "Smart growth strategies for UK businesses. Focus on profit margins, not just revenue. Sustainable growth tactics explained.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "grow-business-profitably-uk",
      popular: true
    },
    {
      id: 19,
      title: "Revenue vs Profit: What Growing Businesses Get Wrong",
      excerpt: "Understand the critical difference between revenue and profit. Why revenue growth alone can destroy your business.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "revenue-vs-profit-uk",
      popular: true
    },
    {
      id: 20,
      title: "Hiring Your First Employee in the UK (2026 Guide)",
      excerpt: "Complete guide to hiring your first employee. PAYE setup, payroll, auto-enrollment, employment law explained.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "hiring-first-employee-uk"
    },
    {
      id: 21,
      title: "When Should You Switch to a Limited Company? (UK 2026)",
      excerpt: "Know when to incorporate. Profit thresholds, tax savings, timing, and incorporation process explained.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "when-to-go-limited-uk"
    },
    {
      id: 22,
      title: "The Hidden Risks of Growing Your Business Too Fast",
      excerpt: "Understand the dangers of rapid growth. Cash flow crises, quality problems, and sustainable scaling strategies.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-growth-risks-uk"
    },
    {
      id: 23,
      title: "VAT Planning Strategies for Growing Businesses (UK 2026)",
      excerpt: "Smart VAT planning strategies. Flat Rate Scheme, voluntary registration, threshold management explained.",
      category: "Growing your company",
      date: "10 April 2026",
      readTime: "8 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "vat-planning-uk"
    },
    {
      id: 24,
      title: "Staying Compliant with HMRC: What Every Business Owner Must Know (UK 2026)",
      excerpt: "Complete guide to HMRC compliance for UK businesses. Learn filing deadlines, record-keeping requirements, and how to avoid penalties in 2026.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "hmrc-compliance-guide-uk"
    },
    {
      id: 25,
      title: "HMRC Deadlines You Cannot Afford to Miss (UK 2026 Guide)",
      excerpt: "Complete guide to all HMRC deadlines for UK businesses in 2026. Self Assessment, VAT, Corporation Tax, PAYE deadlines with penalty costs.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "hmrc-deadlines-uk"
    },
    {
      id: 26,
      title: "Bookkeeping Basics for Compliance (UK Small Business Guide)",
      excerpt: "Essential bookkeeping practices for HMRC compliance. Digital records, expense tracking, and MTD preparation for UK businesses.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "bookkeeping-compliance-uk"
    },
    {
      id: 27,
      title: "Making Tax Digital (MTD): What You Must Do Before April 2026",
      excerpt: "MTD for Income Tax becomes mandatory from April 2026. Quarterly submissions, digital software requirements, and preparation steps.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "making-tax-digital-compliance"
    },
    {
      id: 28,
      title: "VAT Compliance: Common Mistakes That Cost Businesses Money",
      excerpt: "Avoid costly VAT compliance errors. Wrong VAT rates, late filings, reclaim mistakes, and MTD requirements explained for UK businesses.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "7 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "vat-compliance-mistakes"
    },
    {
      id: 29,
      title: "Corporation Tax Compliance Explained (UK 2026)",
      excerpt: "Complete Corporation Tax compliance guide for UK limited companies. CT600 filing, payment deadlines, accurate accounts, and HMRC requirements.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "corporation-tax-compliance"
    },
    {
      id: 30,
      title: "Self Assessment Compliance: Avoid Costly Errors",
      excerpt: "Complete Self Assessment compliance guide for UK taxpayers. Registration deadlines, what to declare, allowable expenses, payment on account explained.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "self-assessment-compliance"
    },
    {
      id: 31,
      title: "HMRC Penalties Explained: How to Avoid Them",
      excerpt: "Complete guide to HMRC penalties for UK businesses. Late filing, late payment, incorrect returns. Penalty amounts, appeals, and how to avoid them.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "hmrc-penalties-guide"
    },
    {
      id: 32,
      title: "Record Keeping Requirements for UK Businesses (2026 Guide)",
      excerpt: "Complete guide to UK business record keeping requirements. What to keep, how long (5-6 years), digital vs paper, HMRC compliance.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "record-keeping-uk"
    },
    {
      id: 33,
      title: "Compliance Checklist for Small Businesses (UK 2026)",
      excerpt: "Complete HMRC compliance checklist for UK small businesses. Monthly, quarterly, and annual tasks to stay compliant and avoid penalties.",
      category: "Staying Compliant",
      date: "6 April 2026",
      readTime: "6 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "compliance-checklist-uk"
    },
    {
      id: 34,
      title: "How to Improve Cash Flow in Your Business (UK Guide)",
      excerpt: "Practical strategies to improve cash flow in your UK business. Expert advice from Wolverhampton accountants on managing payments, reducing costs, and maintaining healthy cash reserves.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "improve-cash-flow-uk"
    },
    {
      id: 35,
      title: "Why Most Small Businesses Struggle to Grow (And How to Fix It)",
      excerpt: "Discover why most UK small businesses fail to scale and the exact steps to break through growth barriers. Expert advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-growth-problems-uk"
    },
    {
      id: 36,
      title: "Pricing Strategies for Small Businesses (UK 2026 Guide)",
      excerpt: "Practical pricing strategies for UK small businesses. How to set prices that maximize profit without losing customers. Expert advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "pricing-strategy-uk"
    },
    {
      id: 37,
      title: "How to Increase Profit Without Increasing Revenue",
      excerpt: "Practical strategies to boost profit margins without chasing more sales. Expert cost reduction and efficiency advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "increase-profit-without-revenue"
    },
    {
      id: 38,
      title: "Financial Habits of Successful Business Owners",
      excerpt: "Discover the financial habits that separate successful business owners from struggling ones. Expert advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "financial-habits-business-owners"
    },
    {
      id: 39,
      title: "When Should You Invest Back Into Your Business?",
      excerpt: "Expert guidance on when and where to reinvest business profits for maximum growth. Strategic investment advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "invest-in-business-uk"
    },
    {
      id: 40,
      title: "How to Build a Financially Stable Business (UK Guide)",
      excerpt: "Practical steps to build financial stability and resilience in your UK business. Expert advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "financial-stability-business"
    },
    {
      id: 41,
      title: "Business Growth vs Profit: What Matters More?",
      excerpt: "Should you prioritize revenue growth or profit margins? Expert guidance for UK business owners from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "growth-vs-profit-uk"
    },
    {
      id: 42,
      title: "How to Stop Cash Leaks in Your Business",
      excerpt: "Identify and eliminate hidden cash drains in your UK business. Expert cost-saving strategies from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "9 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "stop-cash-leaks-uk"
    },
    {
      id: 43,
      title: "Signs Your Business Needs Financial Restructuring",
      excerpt: "Recognize the warning signs that your UK business needs financial restructuring. Expert turnaround advice from Wolverhampton accountants.",
      category: "Business Advice",
      date: "7 April 2026",
      readTime: "10 min read",
      author: "Brightson Accounting",
      credentials: "FCCA Qualified",
      slug: "business-restructuring-uk"
    }
  ];

  const categories = [
    "All Posts",
    "Tax Planning",
    "Tax Advisory",
    "Self Assessment",
    "Compliance",
    "Business Advice",
    "Starting a Business",
    "Growing your company",
    "Staying Compliant"
  ];

  const popularPosts = blogPosts.filter(post => post.popular);

  // Quick action mappings
  const quickHelpMappings = {
    "Reduce my tax": "/blog/reduce-corporation-tax-uk",
    "File my self assessment": "/blog/self-assessment-guide-first-time",
    "Start a business": "/start-a-business-uk",
    "Avoid HMRC penalties": "/blog/tax-deadlines-small-businesses-2026-27"
  };

  const quickHelp = [
    { label: "Reduce my tax", icon: TrendingUp, url: quickHelpMappings["Reduce my tax"] },
    { label: "File my self assessment", icon: FileText, url: quickHelpMappings["File my self assessment"] },
    { label: "Start a business", icon: Award, url: quickHelpMappings["Start a business"] },
    { label: "Avoid HMRC penalties", icon: Shield, url: quickHelpMappings["Avoid HMRC penalties"] }
  ];

  // Filter posts by category
  const filteredPosts = activeCategory === "All Posts" 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  // Handle category click
  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    trackEvent('category_tab_click', {
      label: category,
      location: 'blog_page'
    });
  };

  // Handle quick action click
  const handleQuickActionClick = (label, url) => {
    trackEvent('quick_action_click', {
      label: label,
      location: 'blog_page',
      destination: url
    });
  };

  const explainers = [
    { title: "What is Corporation Tax?", desc: "19% tax on limited company profits" },
    { title: "What is VAT threshold?", desc: "£85,000 annual turnover" },
    { title: "What expenses can you claim?", desc: "Business costs to reduce tax" }
  ];

  const upcomingDeadlines = [
    { date: "31 Jan 2026", event: "Self Assessment deadline" },
    { date: "19 Feb 2026", event: "Corporation Tax payment" },
    { date: "31 Mar 2026", event: "End of tax year" }
  ];

  const contentByStage = [
    { stage: "Starting a business", icon: "🚀", count: 8 },
    { stage: "Growing your company", icon: "📈", count: 12 },
    { stage: "Managing taxes", icon: "💰", count: 15 },
    { stage: "Staying compliant", icon: "✅", count: 10 }
  ];

  return (
    <div className="blog-page">
      <SEO
        title="Accounting & Tax Blog | Expert Advice for Wolverhampton Businesses"
        description="Free accounting and tax advice for small businesses in Wolverhampton. Expert tips on tax planning, HMRC compliance, self assessment, and business growth from FCCA-qualified accountants."
        canonical="/blog"
        keywords="accounting blog, tax advice Wolverhampton, small business tax tips, HMRC advice, self assessment help"
        schema={breadcrumbSchema}
      />

      {/* Enhanced Hero Section */}
      <section className="blog-hero-premium">
        <div className="hero-grain-overlay"></div>
        <div className="container">
          <div className="hero-content-left">
            <span className="hero-label">INSIGHTS & GUIDES</span>
            <h1 className="hero-title-large">Accounting & Tax Insights</h1>
            <p className="hero-subtitle-refined">
              Expert advice, tax tips, and business guidance from FCCA-qualified accountants
            </p>
            <p className="hero-trust-line">
              Trusted by UK small businesses & limited companies
            </p>
          </div>
        </div>
      </section>

      {/* Authority Strip */}
      <section className="authority-strip">
        <div className="container">
          <div className="authority-items">
            <span className="authority-item">FCCA Qualified</span>
            <span className="authority-divider">•</span>
            <span className="authority-item">15+ Years Experience</span>
            <span className="authority-divider">•</span>
            <span className="authority-item">UK Tax Specialists</span>
            <span className="authority-divider">•</span>
            <span className="authority-item">Wolverhampton Based</span>
          </div>
        </div>
      </section>

      {/* Quick Value Entry */}
      <section className="quick-value">
        <div className="container">
          <h2 className="section-heading-center">What do you need help with?</h2>
          <div className="quick-help-grid">
            {quickHelp.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Link
                  key={index}
                  to={item.url}
                  className="quick-help-card"
                  onClick={() => handleQuickActionClick(item.label, item.url)}
                >
                  <IconComponent size={24} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="search-section">
        <div className="container">
          <div className="search-bar">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search tax topics or questions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Category Navigation - Refined */}
      <section className="category-nav-refined">
        <div className="container">
          <div className="category-links">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`category-link ${activeCategory === category ? 'active' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="featured-article">
        <div className="container">
          <article className="featured-card">
            <div className="featured-image">
              <div className="featured-placeholder">
                <FileText size={64} />
              </div>
            </div>
            <div className="featured-content">
              <span className="featured-badge">FEATURED</span>
              <h2 className="featured-title">{featuredPost.title}</h2>
              <p className="featured-excerpt">{featuredPost.excerpt}</p>
              <div className="featured-meta">
                <div className="author-info">
                  <Award size={18} />
                  <span>{featuredPost.author}, {featuredPost.credentials}</span>
                </div>
                <div className="post-meta">
                  <Calendar size={16} />
                  <span>{featuredPost.date}</span>
                  <Clock size={16} />
                  <span>{featuredPost.readTime}</span>
                </div>
              </div>
              <a href={`/blog/${featuredPost.slug}`} className="featured-cta">
                Read Article <ArrowRight size={20} />
              </a>
            </div>
          </article>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="popular-section">
        <div className="container">
          <h2 className="section-heading">Most Read by Business Owners</h2>
          <div className="popular-grid">
            {popularPosts.map((post, index) => (
              <article key={post.id} className="popular-card">
                <div className="popular-number">{index + 1}</div>
                <div className="popular-content">
                  {post.featuredInSection && (
                    <span className="featured-in-section-badge">FEATURED</span>
                  )}
                  <span className="post-category-minimal">{post.category}</span>
                  <h3 className="popular-title">{post.title}</h3>
                  <div className="post-meta-minimal">
                    <span>{post.readTime}</span>
                    <span>•</span>
                    <span>{post.author}</span>
                  </div>
                  <a href={`/blog/${post.slug}`} className="popular-read-link">
                    Read Article <ArrowRight size={16} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Soft CTA Block 1 */}
      <section className="soft-cta-block">
        <div className="container">
          <div className="soft-cta-content">
            <p className="soft-cta-question">Need help with this?</p>
            <p className="soft-cta-text">Speak to an FCCA-qualified accountant</p>
            <Link to="/contact" className="soft-cta-button">
              Book a free consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Content by Business Stage */}
      <section className="content-stage">
        <div className="container">
          <h2 className="section-heading">Guides based on where you are</h2>
          <div className="stage-grid">
            {contentByStage.map((item, index) => (
              <div key={index} className="stage-card">
                <span className="stage-icon">{item.icon}</span>
                <h3 className="stage-title">{item.stage}</h3>
                <span className="stage-count">{item.count} articles</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles Grid - Upgraded */}
      <section className="articles-section">
        <div className="container">
          <h2 className="section-heading">
            {activeCategory === "All Posts" ? "Latest Insights" : `${activeCategory} Articles`}
          </h2>
          {filteredPosts.length > 0 ? (
            <div className="articles-grid-refined">
              {filteredPosts.map((post) => (
                <article key={post.id} className="article-card-refined">
                  <span className="article-category-tag">{post.category}</span>
                  <h3 className="article-title-prominent">{post.title}</h3>
                  <p className="article-excerpt-light">{post.excerpt}</p>
                  <div className="article-author">
                    <Award size={16} />
                    <span>{post.author}, {post.credentials}</span>
                  </div>
                  <div className="article-meta-subtle">
                    <span>{post.date}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>
                  <a href={`/blog/${post.slug}`} className="article-link-refined">
                    Read Article <ArrowRight size={18} />
                  </a>
                </article>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <p>No articles found in this category yet. Check back soon!</p>
              <button 
                className="category-link" 
                onClick={() => handleCategoryClick("All Posts")}
              >
                View All Posts
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Explainer Cards */}
      <section className="explainers-section">
        <div className="container">
          <h2 className="section-heading">Quick Answers</h2>
          <div className="explainers-grid">
            {explainers.map((item, index) => (
              <div key={index} className="explainer-card">
                <h3 className="explainer-question">{item.title}</h3>
                <p className="explainer-answer">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Deadlines */}
      <section className="deadlines-section">
        <div className="container">
          <h2 className="section-heading">Upcoming Deadlines</h2>
          <div className="deadlines-list">
            {upcomingDeadlines.map((item, index) => (
              <div key={index} className="deadline-item">
                <span className="deadline-date">{item.date}</span>
                <span className="deadline-event">{item.event}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <div className="container">
          <h2 className="section-heading-center">Why business owners read our insights</h2>
          <div className="trust-grid">
            <div className="trust-point">
              <h3>Practical guidance</h3>
              <p>Real-world advice you can action immediately</p>
            </div>
            <div className="trust-point">
              <h3>Clear explanations</h3>
              <p>Complex tax rules made simple</p>
            </div>
            <div className="trust-point">
              <h3>Written by qualified accountants</h3>
              <p>FCCA-certified professionals with 15+ years experience</p>
            </div>
            <div className="trust-point">
              <h3>UK regulation focus</h3>
              <p>Up-to-date with HMRC and Companies House requirements</p>
            </div>
          </div>
        </div>
      </section>

      {/* Email Capture */}
      <section className="email-capture">
        <div className="container">
          <div className="capture-content">
            <h2>Get tax updates that matter</h2>
            <p>Monthly insights, deadline reminders, and practical tips</p>
            <div className="capture-buttons">
              <Link to="/contact" className="capture-btn">
                <Mail size={20} />
                Join newsletter
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <div className="final-cta-content">
            <h2>Need Personal Accounting Advice?</h2>
            <p>Our FCCA-qualified accountants provide tailored guidance for your specific business situation.</p>
            <Link to="/contact" className="final-cta-button">
              <Mail size={20} />
              Get Expert Advice
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
